/** @jsxImportSource @emotion/react */
import { useContext } from "react";

import { css } from "@emotion/react";

import LanguageContext from "@/context/language/LanguageContext";

import PickerInputValue from "../../shared/components/PickerInputValue";
import { input, rootCSS } from "../../shared/styles/useDatePickerStyles";
import { VIEW_MODE } from "../../shared/types";
import BaseDayPickersPopover from "../shared/BaseDayPickersPopover";
import MonthSelectView from "../shared/MonthSelectView";

import DayView from "./components/DayView";
import WeekdayHeader from "./components/WeekdayHeader";
import { useDayRangePicker } from "./hooks/useDayRangePicker";
import { DayRangePickerProps } from "./types";

const DayRangePicker = ({
  minDate,
  maxDate,
  disabled,
  externalValue,
  customStyle,
  disableSameDay,
  smallerInput,
  shouldDisableDate,
  onChange,
  onClear
}: DayRangePickerProps) => {
  const { language } = useContext(LanguageContext);

  const {
    year,
    month,
    open,
    viewMode,
    viewVisible,
    slideClass,
    anchorRef,
    pickerRef,
    maxPickerHeight,
    startDay,
    endDay,
    hoverDay,
    picking,
    noStartDay,
    hasClearableValue,
    isAtMinMonth,
    isAtMaxMonth,
    getHeaderLabel,
    setHoverDay,
    openPicker,
    closePicker,
    handleDayClick,
    handleNavigateBack,
    handleNavigateForward,
    handleHeaderClick,
    handleMonthSelect,
    handleYearSelect,
    handleClear,
    cancelHoverClear,
    getDisplayValue
  } = useDayRangePicker({
    minDate,
    maxDate,
    disableSameDay,
    disabled,
    externalValue,
    onChange
  });

  return (
    <div css={css(rootCSS(disabled), customStyle)}>
      <div
        onClick={openPicker}
        ref={anchorRef}
        css={css(input(open, disabled, smallerInput))}
        data-testid="day-range-picker-input"
      >
        <PickerInputValue
          displayValue={getDisplayValue()}
          isPlaceholder={noStartDay}
          disabled={disabled}
          hasClearableValue={hasClearableValue}
          onClear={onClear ?? handleClear}
        />
      </div>

      <BaseDayPickersPopover
        open={open}
        anchorRef={anchorRef}
        pickerRef={pickerRef}
        maxPickerHeight={maxPickerHeight}
        viewMode={viewMode}
        viewVisible={viewVisible}
        slideClass={slideClass}
        year={year}
        isAtMinMonth={isAtMinMonth}
        isAtMaxMonth={isAtMaxMonth}
        getHeaderLabel={getHeaderLabel}
        onClose={closePicker}
        onHeaderClick={handleHeaderClick}
        onNavigateBack={handleNavigateBack}
        onNavigateForward={handleNavigateForward}
        onYearSelect={handleYearSelect}
        minDate={minDate}
        maxDate={maxDate}
      >
        {viewMode === VIEW_MODE.Month ? (
          <MonthSelectView
            year={year}
            month={month}
            minDate={minDate}
            maxDate={maxDate}
            onMonthSelect={handleMonthSelect}
          />
        ) : (
          <>
            <WeekdayHeader language={language} />
            <DayView
              year={year}
              month={month}
              startDay={startDay}
              endDay={endDay}
              hoverDay={hoverDay}
              picking={picking}
              minDate={minDate}
              maxDate={maxDate}
              disableSameDay={disableSameDay}
              shouldDisableDate={shouldDisableDate}
              onDayClick={handleDayClick}
              cancelHoverClear={cancelHoverClear}
              setHoverDay={setHoverDay}
            />
          </>
        )}
      </BaseDayPickersPopover>
    </div>
  );
};

export default DayRangePicker;
